/**
 * LEVELS AS DATUMS (docs/MODELER.md "levels as datums" round — Matt's field method:
 * placement by bottom/top constraints, story-datum based). Two things live here:
 *
 * 1. The SITE DATUM — one designated HOME level plus its REAL-WORLD elevation. The doc
 *    keeps modelling in project-relative feet (every LevelRecord.elevationFt, every
 *    element z, all geometry) and the site datum is a single stored OFFSET applied at
 *    READ time, so re-benchmarking a project to a corrected survey elevation moves no
 *    geometry — it changes one number. Stored in the `settings` map (undo-tracked like
 *    every other settings key), never duplicated per level.
 * 2. The derived LEVEL DATUM list — name, project elevation, real elevation, and the
 *    story height to the level above — ONE derivation shared by the Level Manager, the
 *    2D datum renderers (sections/elevations/sheets/PDF), and spot elevations. Nothing
 *    here is stored; heights are always `next.elevationFt - this.elevationFt`.
 */
import { normalizeOpeningStyle, type OpeningStyleSettings } from './openingStyle'
import { normalizeTrimStyle, type TrimStyleSettings, type EaveStyle } from './trimStyle'
import { normalizeStyleSets, STYLE_SETS_KEY, BASE_SET_ID, type StyleSet, type StyleSetGroup, type StyleSetsSettings } from './styleSets'
import { nanoid } from 'nanoid'
import type * as Y from 'yjs'
import type { ElementId, GridId, GridRecord, LevelId, Pt2 } from './types'
import { getGrid, getLevel, getStyleSets, listGrids, listLevels, maps, recordToYMap, transact, type ModelDoc } from './doc'
import { normalizeStructural, normalizeWindowRake, type StructuralSettings, type WindowRakeSettings } from './params'
import { newGridId, newHeadLockId } from './ids'

/** settings key — one per model */
const KEY = 'siteDatum'

export interface SiteDatum {
  /** the level the real-world elevation is measured AT (null = no datum set) */
  homeLevelId: LevelId | null
  /** that level's real-world elevation, feet */
  realElevationFt: number
}

/** The stored site datum, or null when the model has never had one set. */
export function getSiteDatum(model: ModelDoc): SiteDatum | null {
  const v = maps(model).settings.get(KEY) as Partial<SiteDatum> | undefined
  if (!v || typeof v !== 'object') return null
  return {
    homeLevelId: (v.homeLevelId ?? null) as LevelId | null,
    realElevationFt: typeof v.realElevationFt === 'number' ? v.realElevationFt : 0,
  }
}

/** Patch-merge the site datum; `null` clears it entirely (back to project-relative only). */
export function setSiteDatum(model: ModelDoc, patch: Partial<SiteDatum> | null): void {
  transact(model, () => {
    const s = maps(model).settings
    if (patch === null) { s.delete(KEY); return }
    const cur = getSiteDatum(model) ?? { homeLevelId: null, realElevationFt: 0 }
    s.set(KEY, { ...cur, ...patch })
  })
}

// ---------- project STRUCTURAL settings (FOOTINGS follow-up, 2026-08-09) ----------
// Same settings-map home as the site datum: one number per project, undo-tracked.
// See params.ts#StructuralSettings for why coverage is a PROJECT standard, not a
// footing-type param.

const STRUCTURAL_KEY = 'structural'

/** The project's structural standards — defaults when never set. */
export function getStructuralSettings(model: ModelDoc): StructuralSettings {
  return normalizeStructural(maps(model).settings.get(STRUCTURAL_KEY))
}

/** Patch-merge the project structural standards. One transact = one undo step. */
export function setStructuralSettings(model: ModelDoc, patch: Partial<StructuralSettings>): void {
  transact(model, () => {
    const s = maps(model).settings
    s.set(STRUCTURAL_KEY, { ...getStructuralSettings(model), ...patch })
  })
}

// ---------- project WINDOW RAKE default (RAKED WINDOW HEADS round, 2026-09-22) ----------
// Same settings-map home, same shape as structural above — one slope per project,
// undo-tracked. See params.ts#WindowRakeSettings for why this is a project standard,
// not a window-type param.

const WINDOW_RAKE_KEY = 'windowRake'

/** The project's default rake slope — defaults when never set. */
export function getWindowRakeSettings(model: ModelDoc): WindowRakeSettings {
  return normalizeWindowRake(maps(model).settings.get(WINDOW_RAKE_KEY))
}

/** Patch-merge the project default rake slope. One transact = one undo step. */
export function setWindowRakeSettings(model: ModelDoc, patch: Partial<WindowRakeSettings>): void {
  transact(model, () => {
    const s = maps(model).settings
    s.set(WINDOW_RAKE_KEY, { ...getWindowRakeSettings(model), ...patch })
  })
}

/**
 * The offset from project-relative feet to real-world feet, or null when no usable
 * datum exists (never set, cleared, or its home level was deleted — a dangling home
 * level reads as NO datum rather than silently measuring from the wrong story).
 */
export function datumOffsetFt(model: ModelDoc): number | null {
  const d = getSiteDatum(model)
  if (!d || !d.homeLevelId) return null
  const home = getLevel(model, d.homeLevelId)
  if (!home) return null
  return d.realElevationFt - home.elevationFt
}

/** A project-relative elevation in real-world feet, or null when there's no datum. */
export function realElevationOf(model: ModelDoc, projectFt: number): number | null {
  const off = datumOffsetFt(model)
  return off === null ? null : projectFt + off
}

/**
 * Real-world ↔ project-relative for the topo element (docs/MODELER.md "TOPOGRAPHY
 * element" — real-world-at-rest round): unlike `realElevationOf`, these never return
 * null — an unset datum reads as a zero offset, so real-world and project-relative
 * are simply the same number until a datum is dialed in. Topo's stored elevations
 * (TopoPoint.zFt, TopoModifier.elevFt/zs — see types.ts) are real-world; every
 * geometry consumer needs them converted to the model's project-relative frame.
 */
export function toProjectFt(model: ModelDoc, realFt: number): number {
  return realFt - (datumOffsetFt(model) ?? 0)
}

/** The inverse of `toProjectFt` — project-relative feet back to real-world. */
export function toRealFt(model: ModelDoc, projectFt: number): number {
  return projectFt + (datumOffsetFt(model) ?? 0)
}

/**
 * ELEVATIONS read as DECIMAL FEET to the nearest hundredth (Matt, 2026-08-15) —
 * the survey/civil convention, distinct from LENGTHS which stay feet-and-inches
 * (`formatFtIn`). One formatter so every elevation display agrees.
 */
export function formatElevFt(v: number): string {
  const r = Math.round(v * 100) / 100
  return `${(Object.is(r, -0) ? 0 : r).toFixed(2)}'`
}

/**
 * The two numbers every elevation display shows: `primary` is what the drawing reads
 * (real-world once a datum exists, else project-relative) and `secondary` is the other
 * one, or null when there's no datum to have two of. Renderers decide layout; this
 * decides which number leads. Elevations format as decimal feet, two decimals.
 */
export function elevationLabels(model: ModelDoc, projectFt: number): { primary: string; secondary: string | null } {
  const real = realElevationOf(model, projectFt)
  return real === null
    ? { primary: formatElevFt(projectFt), secondary: null }
    : { primary: formatElevFt(real), secondary: formatElevFt(projectFt) }
}

export interface LevelDatum {
  id: LevelId
  name: string
  /** project-relative, the doc's own unit */
  elevationFt: number
  /** real-world, or null when no site datum is set */
  realFt: number | null
  /** to the next level UP, or null for the topmost level */
  heightToNextFt: number | null
  isHome: boolean
}

// ---------- structural grids (r23 — the datum family's second member) ----------
// Model-space datum LINES: drawn once in plan, appearing in every plan and in
// perpendicular vertical views with a bubble (annotation/datums.js#datumLinePrims is
// the ONE renderer — a grid only supplies where its line comes from). Grids are
// model-LEVEL records (not per-level, not per-view): a column line is true on every
// story, which is the entire point of a grid.

/**
 * The next unused auto-name for a grid drawn a→b — the drafting convention:
 * LETTERS (A, B, C…) for lines running mostly north–south (vertical in plan),
 * NUMBERS (1, 2, 3…) for lines running mostly east–west. Renameable at will;
 * this only picks a sensible fresh label.
 */
export function nextGridName(model: ModelDoc, a: Pt2, b: Pt2): string {
  const vertical = Math.abs(b[1] - a[1]) >= Math.abs(b[0] - a[0])
  const used = new Set(listGrids(model).map((g) => g.name))
  if (vertical) {
    for (let i = 0; i < 702; i++) {   // A…Z, AA…ZZ
      const name = i < 26
        ? String.fromCharCode(65 + i)
        : String.fromCharCode(65 + Math.floor(i / 26) - 1) + String.fromCharCode(65 + (i % 26))
      if (!used.has(name)) return name
    }
  }
  for (let i = 1; ; i++) {
    if (!used.has(String(i))) return String(i)
  }
}

/** the n-th grid letter (0 → A … 25 → Z, 26 → AA …) — the same sequence nextGridName uses */
function gridLetter(i: number): string {
  return i < 26 ? String.fromCharCode(65 + i)
    : String.fromCharCode(65 + Math.floor(i / 26) - 1) + String.fromCharCode(65 + (i % 26))
}

/**
 * RENUMBER GRIDS (Matt, 2026-10-08: "I need a method for quickly renumbering grids"):
 * one command, one undo step. Grids are sorted in the drawing's own frame (the
 * dominant grid direction, so a rotated building numbers as drawn) into two families:
 * lines running up the sheet are LETTERED left → right (A, B, C …); lines running across
 * are NUMBERED top → bottom (1, 2, 3 …) — the convention nextGridName already assigns and
 * Santiago's set reads in. Which family takes letters follows the model: if most of the
 * lines running across already carry letters, the families swap. Grids at another angle
 * keep their names (a name a renumbered grid now uses gets a ′ suffix). Returns how many
 * names changed.
 */
export function renumberGrids(model: ModelDoc): number {
  const grids = listGrids(model)
  const next = gridRenumberPlan(model)
  let changed = 0
  transact(model, () => {
    for (const g of grids) {
      const n = next.get(g.id)
      if (n == null || n === g.name) continue
      maps(model).grids.get(g.id)?.set('name', n)
      changed++
    }
  })
  return changed
}

/**
 * GRID MANAGER (2026-10-13): the model's grids sorted the way renumberGrids reads them —
 * LETTERED lines left → right, NUMBERED lines top → bottom, anything at another angle in
 * `other` — plus each line's position along its family's axis (feet, in the drawing
 * frame: left → right for lettered, top → bottom for numbered) so the manager can show
 * bay spacing. Read-only; nothing is written.
 */
export interface GridFamilies {
  lettered: GridRecord[]
  numbered: GridRecord[]
  other: GridRecord[]
  /** grid id → position along its family's reading direction (ft, increasing in reading order) */
  position: Map<GridId, number>
  /** the unit vector a lettered grid's position is measured along (a numbered grid's is its left-normal) */
  frameU: Pt2
  frameV: Pt2
}

export function gridFamilies(model: ModelDoc): GridFamilies {
  const grids = listGrids(model)
  const position = new Map<GridId, number>()
  if (!grids.length) return { lettered: [], numbered: [], other: [], position, frameU: [1, 0], frameV: [0, 1] }
  // the drawing frame: the median grid direction folded into [0°, 90°)
  const folded = grids.map((g) => {
    const t = Math.atan2(g.b[1] - g.a[1], g.b[0] - g.a[0])
    return ((t % (Math.PI / 2)) + Math.PI / 2) % (Math.PI / 2)
  }).sort((x, y) => x - y)
  const th = folded[Math.floor(folded.length / 2)]
  const u: Pt2 = [Math.cos(th), Math.sin(th)], v: Pt2 = [-Math.sin(th), Math.cos(th)]
  const across: GridRecord[] = [], upward: GridRecord[] = [], other: GridRecord[] = []
  for (const g of grids) {
    const dx = g.b[0] - g.a[0], dy = g.b[1] - g.a[1], L = Math.hypot(dx, dy) || 1
    const cu = Math.abs((dx * u[0] + dy * u[1]) / L)
    if (cu > 0.995) across.push(g)
    else if (cu < 0.1) upward.push(g)
    else other.push(g)
  }
  const mid = (g: GridRecord): Pt2 => [(g.a[0] + g.b[0]) / 2, (g.a[1] + g.b[1]) / 2]
  const along = (g: GridRecord, w: Pt2) => { const m = mid(g); return m[0] * w[0] + m[1] * w[1] }
  for (const g of upward) position.set(g.id, along(g, u))        // left → right
  for (const g of across) position.set(g.id, -along(g, v))       // top → bottom
  const byPos = (a: GridRecord, b: GridRecord) => (position.get(a.id) ?? 0) - (position.get(b.id) ?? 0)
  upward.sort(byPos)
  across.sort(byPos)
  const isLetter = (n: string) => /^[A-Za-z]+$/.test(n.trim())
  const swap = across.filter((g) => isLetter(g.name)).length > across.length / 2
    && upward.filter((g) => !isLetter(g.name)).length >= upward.length / 2
  return {
    lettered: swap ? across : upward,
    numbered: swap ? upward : across,
    other, position, frameU: u, frameV: v,
  }
}

/** what renumberGrids WOULD name every grid — the manager's preview; writes nothing */
export function gridRenumberPlan(model: ModelDoc): Map<GridId, string> {
  const { lettered, numbered, other } = gridFamilies(model)
  const next = new Map<GridId, string>()
  lettered.forEach((g, i) => next.set(g.id, gridLetter(i)))
  numbered.forEach((g, i) => next.set(g.id, String(i + 1)))
  const taken = new Set(next.values())
  for (const g of other) {
    let n = g.name
    while (taken.has(n)) n += '′'
    next.set(g.id, n)
    taken.add(n)
  }
  return next
}

/**
 * GRID MANAGER: slide one grid PERPENDICULAR to itself so it sits `targetFt` from
 * `fromId` (measured square to the lines, both grids parallel). One undo step; a pinned
 * grid refuses (false). The precision hook lands the result on the 1/64" lattice.
 */
export function setGridSpacing(model: ModelDoc, id: GridId, fromId: GridId, targetFt: number): boolean {
  const g = getGrid(model, id), f = getGrid(model, fromId)
  if (!g || !f || g.pinned === true) return false
  const dx = g.b[0] - g.a[0], dy = g.b[1] - g.a[1], L = Math.hypot(dx, dy)
  if (L < 1e-9) return false
  const n: Pt2 = [-dy / L, dx / L]
  const gm: Pt2 = [(g.a[0] + g.b[0]) / 2, (g.a[1] + g.b[1]) / 2]
  const fm: Pt2 = [(f.a[0] + f.b[0]) / 2, (f.a[1] + f.b[1]) / 2]
  const cur = (gm[0] - fm[0]) * n[0] + (gm[1] - fm[1]) * n[1]
  const sign = cur < 0 ? -1 : 1
  const delta = sign * Math.abs(targetFt) - cur
  if (Math.abs(delta) < 1e-9) return true
  return moveGrid(model, id, n[0] * delta, n[1] * delta)
}

export function createGrid(
  model: ModelDoc,
  input: { a: Pt2; b: Pt2; name?: string },
): GridId {
  const id = newGridId()
  const name = input.name?.trim() || nextGridName(model, input.a, input.b)
  transact(model, () => {
    maps(model).grids.set(id, recordToYMap({
      id, name, a: [input.a[0], input.a[1]], b: [input.b[0], input.b[1]],
    }))
  })
  return id
}

export function updateGrid(
  model: ModelDoc,
  id: GridId,
  patch: Partial<Pick<GridRecord, 'name' | 'a' | 'b' | 'heads' | 'zExtentFt' | 'lockA' | 'lockB' | 'headOff' | 'pinned'>>,
): boolean {
  const m = maps(model).grids.get(id)
  if (!m) return false
  // GRID PIN round: a pinned grid's LINE never moves (name/heads/extent stay editable,
  // the element pin's "settings stay open" rule)
  if (m.get('pinned') === true && (patch.a !== undefined || patch.b !== undefined)) return false
  transact(model, () => {
    if (patch.pinned !== undefined) { if (patch.pinned) m.set('pinned', true); else m.delete('pinned') }
    if (patch.name !== undefined) m.set('name', patch.name)
    if (patch.a !== undefined) m.set('a', [patch.a[0], patch.a[1]])
    if (patch.b !== undefined) m.set('b', [patch.b[0], patch.b[1]])
    // r25: 'both' IS the default, so storing it would just be noise — clear instead
    if (patch.heads !== undefined) {
      if (!patch.heads || patch.heads === 'both') m.delete('heads')
      else m.set('heads', patch.heads)
    }
    // DATUM UX round: default-never-stored across the board — null/undefined-in-patch
    // clears back to the derived default rather than materializing it
    if (patch.zExtentFt !== undefined) {
      if (!patch.zExtentFt) m.delete('zExtentFt')
      else {
        const lo = Math.min(patch.zExtentFt[0], patch.zExtentFt[1])
        const hi = Math.max(patch.zExtentFt[0], patch.zExtentFt[1])
        m.set('zExtentFt', [lo, hi])
      }
    }
    if (patch.lockA !== undefined) { if (!patch.lockA) m.delete('lockA'); else m.set('lockA', patch.lockA) }
    if (patch.lockB !== undefined) { if (!patch.lockB) m.delete('lockB'); else m.set('lockB', patch.lockB) }
    if (patch.headOff !== undefined) {
      const a = normOff(patch.headOff?.a)
      const b = normOff(patch.headOff?.b)
      if (!a && !b) m.delete('headOff')
      else m.set('headOff', { ...(a ? { a } : {}), ...(b ? { b } : {}) })
    }
  })
  return true
}

/** a head offset small enough to mean "none" clears rather than storing noise */
function normOff(off: Pt2 | undefined | null): Pt2 | null {
  if (!off) return null
  return Math.hypot(off[0], off[1]) < 1e-6 ? null : [off[0], off[1]]
}

/**
 * r25 — the whole grid translated by (dx, dy). One transact, so a move is one undo step
 * whether it came from a drag, a typed distance, or a snapped destination click.
 */
export function moveGrid(model: ModelDoc, id: GridId, dx: number, dy: number): boolean {
  const g = getGrid(model, id)
  if (!g || g.pinned === true) return false
  return updateGrid(model, id, {
    a: [g.a[0] + dx, g.a[1] + dy],
    b: [g.b[0] + dx, g.b[1] + dy],
  })
}

/**
 * r25 — copy a grid to (dx, dy) and return the new id: the pet palette's "Move a copy",
 * which is how a grid SET actually gets drawn (one line, then copies at the bay spacing).
 * The copy takes the next auto-name in its own orientation's series, and carries the
 * source's head setting.
 */
export function copyGrid(model: ModelDoc, id: GridId, dx: number, dy: number): GridId | null {
  const g = getGrid(model, id)
  if (!g) return null
  const a: Pt2 = [g.a[0] + dx, g.a[1] + dy]
  const b: Pt2 = [g.b[0] + dx, g.b[1] + dy]
  const newId = createGrid(model, { a, b })
  // DATUM UX round: a copy carries the source's per-grid deviations — heads, stored
  // z-extent, manual head offsets, AND its head-lock memberships (the copy's ends join
  // the group, so a bay laid by copying stays one locked alignment — the Revit model;
  // an off-alignment copy self-heals on the next locked extend, which re-intersects).
  if (g.heads && g.heads !== 'both') updateGrid(model, newId, { heads: g.heads })
  if (g.zExtentFt || g.headOff || g.lockA || g.lockB) {
    updateGrid(model, newId, {
      ...(g.zExtentFt ? { zExtentFt: [g.zExtentFt[0], g.zExtentFt[1]] as [number, number] } : {}),
      ...(g.headOff ? { headOff: g.headOff } : {}),
      ...(g.lockA ? { lockA: g.lockA } : {}),
      ...(g.lockB ? { lockB: g.lockB } : {}),
    })
  }
  return newId
}

// ---------- DATUM UX round (2026-08-14): head locks + lock-aware end moves ----------

export type GridEnd = 'a' | 'b'

const norm2 = (v: Pt2): Pt2 | null => {
  const l = Math.hypot(v[0], v[1])
  return l < 1e-9 ? null : [v[0] / l, v[1] / l]
}
const cross2 = (u: Pt2, v: Pt2) => u[0] * v[1] - u[1] * v[0]

/** ~1.15° — two grids are "parallel" for head-alignment purposes inside this */
const PARALLEL_TOL = 0.02

/**
 * Other grid ends whose heads ALIGN with (id, end): parallel grids (within
 * PARALLEL_TOL) whose end point shares this end's projection along the grid's own
 * direction within tolFt — the drafting meaning of "the heads line up". These are the
 * candidates a HEAD LOCK groups.
 */
export function alignedGridHeads(
  model: ModelDoc, id: GridId, end: GridEnd, tolFt = 0.1,
): Array<{ id: GridId; end: GridEnd }> {
  const g = getGrid(model, id)
  if (!g) return []
  const d = norm2([g.b[0] - g.a[0], g.b[1] - g.a[1]])
  if (!d) return []
  const p = end === 'a' ? g.a : g.b
  const proj = p[0] * d[0] + p[1] * d[1]
  const out: Array<{ id: GridId; end: GridEnd }> = []
  for (const other of listGrids(model)) {
    if (other.id === id) continue
    const od = norm2([other.b[0] - other.a[0], other.b[1] - other.a[1]])
    if (!od || Math.abs(cross2(d, od)) > PARALLEL_TOL) continue
    for (const oe of ['a', 'b'] as const) {
      const op = oe === 'a' ? other.a : other.b
      if (Math.abs(op[0] * d[0] + op[1] * d[1] - proj) <= tolFt) out.push({ id: other.id, end: oe })
    }
  }
  return out
}

/**
 * HEAD LOCK (the Revit locked-datum-ends model): group the given ends into ONE
 * alignment group — extending any member's locked end moves every member's locked end
 * with it (moveGridEnd). Needs ≥ 2 real ends; if any entry already belongs to a group,
 * the whole set JOINS that group instead of minting a second one. Returns the group id.
 */
export function lockGridHeads(
  model: ModelDoc, entries: Array<{ id: GridId; end: GridEnd }>,
): string | null {
  const valid = entries.filter((e) => getGrid(model, e.id))
  if (valid.length < 2) return null
  let groupId: string | null = null
  for (const e of valid) {
    const g = getGrid(model, e.id)!
    const cur = e.end === 'a' ? g.lockA : g.lockB
    if (cur) { groupId = cur; break }
  }
  const gid = groupId ?? newHeadLockId()
  transact(model, () => {
    for (const e of valid) {
      const m = maps(model).grids.get(e.id)
      if (m) m.set(e.end === 'a' ? 'lockA' : 'lockB', gid)
    }
  })
  return gid
}

/**
 * Unlock ONE head (per-end, the plan's "unlockable per head"). A group left with a
 * single member dissolves — a lock of one is meaningless and would just show a stale
 * padlock.
 */
export function unlockGridHead(model: ModelDoc, id: GridId, end: GridEnd): boolean {
  const g = getGrid(model, id)
  const lockId = end === 'a' ? g?.lockA : g?.lockB
  if (!g || !lockId) return false
  transact(model, () => {
    maps(model).grids.get(id)?.delete(end === 'a' ? 'lockA' : 'lockB')
    const rest = headLockMembers(model, lockId)
    if (rest.length === 1) {
      maps(model).grids.get(rest[0].id)?.delete(rest[0].end === 'a' ? 'lockA' : 'lockB')
    }
  })
  return true
}

/** Every (grid, end) currently in the named lock group, with its head point. */
export function headLockMembers(
  model: ModelDoc, groupId: string,
): Array<{ id: GridId; end: GridEnd; pt: Pt2 }> {
  const out: Array<{ id: GridId; end: GridEnd; pt: Pt2 }> = []
  for (const g of listGrids(model)) {
    if (g.lockA === groupId) out.push({ id: g.id, end: 'a', pt: [g.a[0], g.a[1]] })
    if (g.lockB === groupId) out.push({ id: g.id, end: 'b', pt: [g.b[0], g.b[1]] })
  }
  return out
}

/**
 * Move ONE grid end — and, when that end is head-locked, carry the whole group: the
 * alignment line (through the new end, perpendicular to the dragged grid's own
 * direction) moves, and every member end re-INTERSECTS its own infinite line with it.
 * Members parallel to the alignment line (or zero-length) slide by the projected delta
 * instead. ONE transact = one undo step for the whole group, however it was gestured.
 */
export function moveGridEnd(model: ModelDoc, id: GridId, end: GridEnd, pt: Pt2): boolean {
  const g = getGrid(model, id)
  if (!g || g.pinned === true) return false
  const lockId = end === 'a' ? g.lockA : g.lockB
  const oldPt = end === 'a' ? g.a : g.b
  const fixed = end === 'a' ? g.b : g.a
  transact(model, () => {
    const set = (gid: GridId, e: GridEnd, p: Pt2) => {
      maps(model).grids.get(gid)?.set(e, [p[0], p[1]])
    }
    set(id, end, pt)
    if (!lockId) return
    const d = norm2([pt[0] - fixed[0], pt[1] - fixed[1]])
    if (!d) return
    const n: Pt2 = [-d[1], d[0]]                       // the alignment line's direction
    const delta: Pt2 = [pt[0] - oldPt[0], pt[1] - oldPt[1]]
    for (const other of listGrids(model)) {
      if (other.id === id || other.pinned === true) continue   // a pinned partner stays put
      for (const oe of ['a', 'b'] as const) {
        if ((oe === 'a' ? other.lockA : other.lockB) !== lockId) continue
        const op = oe === 'a' ? other.a : other.b
        const ofix = oe === 'a' ? other.b : other.a
        const od = norm2([op[0] - ofix[0], op[1] - ofix[1]])
        if (od && Math.abs(cross2(od, n)) > 1e-6) {
          const t = cross2([pt[0] - ofix[0], pt[1] - ofix[1]], n) / cross2(od, n)
          set(other.id, oe, [ofix[0] + od[0] * t, ofix[1] + od[1] * t])
        } else if (od) {
          const along = delta[0] * od[0] + delta[1] * od[1]
          set(other.id, oe, [op[0] + od[0] * along, op[1] + od[1] * along])
        } else {
          set(other.id, oe, [op[0] + delta[0], op[1] + delta[1]])
        }
      }
    }
  })
  return true
}

export function deleteGrid(model: ModelDoc, id: GridId): boolean {
  const { grids } = maps(model)
  if (!grids.has(id)) return false
  if (grids.get(id)?.get('pinned') === true) return false   // GRID PIN round
  transact(model, () => { grids.delete(id) })
  return true
}

/** GRID PIN round: pin / unpin a batch of grids in one transaction (one undo step). */
export function setGridsPinned(model: ModelDoc, ids: GridId[], pinned: boolean): void {
  const { grids } = maps(model)
  transact(model, () => {
    for (const id of ids) {
      const m = grids.get(id)
      if (!m) continue
      if (pinned) m.set('pinned', true)
      else m.delete('pinned')
    }
  })
}

export { getGrid, listGrids }

/**
 * Every level as a datum, ordered BOTTOM → TOP by elevation (the stratification order
 * a section reads in, deliberately independent of the `order` field the dropdown uses —
 * see libraryCommands.renumberLevelsByElevation, which keeps the two agreeing).
 */
export function levelDatums(model: ModelDoc): LevelDatum[] {
  const off = datumOffsetFt(model)
  const home = getSiteDatum(model)?.homeLevelId ?? null
  const sorted = listLevels(model).slice().sort((a, b) => a.elevationFt - b.elevationFt)
  return sorted.map((l, i) => ({
    id: l.id,
    name: l.name,
    elevationFt: l.elevationFt,
    realFt: off === null ? null : l.elevationFt + off,
    heightToNextFt: i + 1 < sorted.length ? sorted[i + 1].elevationFt - l.elevationFt : null,
    isHome: l.id === home,
  }))
}

// ---------- project OPENING STYLE (Project Style phase 2, 2026-10-06) ----------
// The project's window & door style — frame system, muntins, setting position, recess, exterior
// trim. See openingStyle.ts for the resolution rule (instance → type-if-stored → project).

const OPENING_STYLE_KEY = 'openingStyle'

export function getOpeningStyle(model: ModelDoc): OpeningStyleSettings {
  return normalizeOpeningStyle(maps(model).settings.get(OPENING_STYLE_KEY))
}

/** Patch-merge the project opening style (null clears a field). One transact = one undo step. */
export function setOpeningStyle(model: ModelDoc, patch: Partial<OpeningStyleSettings>): void {
  transact(model, () => {
    maps(model).settings.set(OPENING_STYLE_KEY, normalizeOpeningStyle({ ...getOpeningStyle(model), ...patch }))
  })
}

// PROJECT STYLE — TRIM & EAVES (trimStyle.ts): the project's trim + eave defaults
const TRIM_STYLE_KEY = 'trimStyle'
export function getTrimStyle(model: ModelDoc): TrimStyleSettings {
  return normalizeTrimStyle(maps(model).settings.get(TRIM_STYLE_KEY))
}
export function setTrimStyle(model: ModelDoc, patch: Partial<TrimStyleSettings>): void {
  const cur = getTrimStyle(model)
  transact(model, () => {
    maps(model).settings.set(TRIM_STYLE_KEY, normalizeTrimStyle({ ...cur, ...patch, eaves: { ...cur.eaves, ...(patch.eaves || {}) } }))
  })
}

// ---------- PHASED SETTINGS SETS (styleSets.ts, docs/bim-plan/32 §3 A) ----------
function writeStyleSets(model: ModelDoc, next: StyleSetsSettings): void {
  maps(model).settings.set(STYLE_SETS_KEY, normalizeStyleSets(next) as unknown as never)
}
/** a group's ORIGINAL values (the base set) — for eaves, Project Settings' own eave fields */
export function baseStyleSetValues(model: ModelDoc, group: StyleSetGroup): Record<string, unknown> {
  if (group === 'eaves') return { ...getTrimStyle(model).eaves }
  return {}
}
/** "New set from this one": a copy of `fromId`'s values (the base when absent), built in
 *  `phaseCreated`. Returns the new set's id. */
export function addStyleSet(
  model: ModelDoc, group: StyleSetGroup, opts: { name: string; phaseCreated: string; fromId?: string | null },
): string {
  const all = getStyleSets(model)
  const src = opts.fromId && opts.fromId !== BASE_SET_ID ? all[group]?.find((s) => s.id === opts.fromId) : null
  const id = nanoid(12)
  transact(model, () => {
    writeStyleSets(model, {
      ...all,
      [group]: [...(all[group] ?? []), {
        id, name: opts.name, phaseCreated: opts.phaseCreated,
        values: { ...(src ? src.values : baseStyleSetValues(model, group)) },
      }],
    })
  })
  return id
}
export function updateStyleSet(
  model: ModelDoc, group: StyleSetGroup, id: string,
  patch: { name?: string; phaseCreated?: string; phaseDemolished?: string | null },
): void {
  const all = getStyleSets(model)
  transact(model, () => {
    writeStyleSets(model, {
      ...all,
      [group]: (all[group] ?? []).map((s) => {
        if (s.id !== id) return s
        const next: StyleSet = { ...s, ...(patch.name != null ? { name: patch.name } : {}), ...(patch.phaseCreated ? { phaseCreated: patch.phaseCreated } : {}) }
        if (patch.phaseDemolished === null) delete next.phaseDemolished
        else if (patch.phaseDemolished) next.phaseDemolished = patch.phaseDemolished
        return next
      }),
    })
  })
}
/** edit one extra set's eave values (the base set is edited through setTrimStyle as always) */
export function setStyleSetEaves(model: ModelDoc, id: string, patch: Partial<EaveStyle>): void {
  const all = getStyleSets(model)
  transact(model, () => {
    writeStyleSets(model, {
      ...all,
      eaves: (all.eaves ?? []).map((s) => (s.id !== id ? s : {
        ...s, values: { ...normalizeTrimStyle({ eaves: { ...(s.values as Partial<EaveStyle>), ...patch } }).eaves },
      })),
    })
  })
}
/** remove an extra set; elements that kept it go back to following the project */
export function removeStyleSet(model: ModelDoc, group: StyleSetGroup, id: string): void {
  const all = getStyleSets(model)
  transact(model, () => {
    writeStyleSets(model, { ...all, [group]: (all[group] ?? []).filter((s) => s.id !== id) })
    maps(model).elements.forEach((yel) => {
      const params = yel.get('params') as Y.Map<unknown> | undefined
      const pins = params?.get?.('styleSetPins') as Record<string, unknown> | undefined
      if (!pins || pins[group] !== id) return
      const { [group]: _drop, ...rest } = pins
      if (Object.keys(rest).length) params!.set('styleSetPins', rest)
      else params!.delete('styleSetPins')
    })
  })
}
/** "Keep <set>" on elements (setId) or back to following the project (null) */
export function setElementStyleSetPin(model: ModelDoc, ids: ElementId[], group: StyleSetGroup, setId: string | null): void {
  transact(model, () => {
    for (const id of ids) {
      const yel = maps(model).elements.get(id)
      const params = yel?.get('params') as Y.Map<unknown> | undefined
      if (!params) continue
      const cur = (params.get('styleSetPins') as Record<string, unknown> | null) ?? {}
      const { [group]: _drop, ...rest } = cur
      const next = setId ? { ...rest, [group]: setId } : rest
      if (Object.keys(next).length) params.set('styleSetPins', next)
      else params.delete('styleSetPins')
    }
  })
}
